import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../ui/dialog';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { toast } from 'sonner';
import { Users, Search, Settings, MailCheck } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagementPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15'
      });
      if (search) params.append('search', search);
      if (filter !== 'all') params.append('filter_role', filter);

      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/stats/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        toast.success('User updated successfully');
        fetchUsers();
        fetchStats();
        setEditDialogOpen(false);
      } else {
        toast.error('Failed to update user');
      }
    } catch (error) {
      toast.error('Error updating user');
    }
  };

  const handleVerifyEmail = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/verify-email`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Email verified');
        fetchUsers();
        fetchStats();
      } else {
        toast.error('Failed to verify email');
      }
    } catch (error) {
      toast.error('Error verifying email');
    }
  };

  const openEditDialog = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setSelectedUser(user);
        setEditDialogOpen(true);
      }
    } catch (error) {
      toast.error('Error loading user details');
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
              <p className="text-xs text-slate-400">Total Users</p>
            </CardContent>
          </Card>
          <Card className="bg-green-900/30 border-green-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-400">{stats.verified}</p>
              <p className="text-xs text-green-300">Verified</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-900/30 border-amber-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{stats.unverified}</p>
              <p className="text-xs text-amber-300">Unverified</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-900/30 border-purple-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-purple-400">{stats.premium}</p>
              <p className="text-xs text-purple-300">Premium</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-900/30 border-blue-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-400">{stats.paid_members}</p>
              <p className="text-xs text-blue-300">Paid</p>
            </CardContent>
          </Card>
          <Card className="bg-cyan-900/30 border-cyan-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-cyan-400">{stats.admins}</p>
              <p className="text-xs text-cyan-300">Admins</p>
            </CardContent>
          </Card>
          <Card className="bg-pink-900/30 border-pink-700">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-pink-400">{stats.operators}</p>
              <p className="text-xs text-pink-300">Operators</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search and Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <Input
                placeholder="Search by email, username..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <Button type="submit" variant="secondary">
                <Search className="w-4 h-4" />
              </Button>
            </form>
            <div className="flex gap-2 flex-wrap">
              {['all', 'admin', 'operator', 'premium', 'paid', 'unverified'].map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? 'default' : 'outline'}
                  onClick={() => { setFilter(f); setPage(1); }}
                  className={filter === f ? 'bg-purple-600' : 'border-slate-600 text-slate-300'}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5" />
            Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">User</TableHead>
                    <TableHead className="text-slate-400">Email Verified</TableHead>
                    <TableHead className="text-slate-400">Membership</TableHead>
                    <TableHead className="text-slate-400">Roles</TableHead>
                    <TableHead className="text-slate-400">Joined</TableHead>
                    <TableHead className="text-slate-400">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} className="border-slate-700">
                      <TableCell>
                        <div>
                          <p className="text-white font-medium">{user.username || user.display_name}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.email_verified ? (
                          <Badge className="bg-green-600 text-xs">Verified</Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-500 text-amber-400 text-xs">
                            Unverified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {user.membership_tier === 'premium' && (
                            <Badge className="bg-purple-600 text-xs">Premium</Badge>
                          )}
                          {user.member_class === 'paid' && (
                            <Badge className="bg-blue-600 text-xs">Paid</Badge>
                          )}
                          {user.beta_membership_active && (
                            <Badge variant="outline" className="border-cyan-500 text-cyan-400 text-xs">Beta</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {user.is_operator_admin && (
                            <Badge className="bg-pink-600 text-xs">Operator</Badge>
                          )}
                          {user.is_sysadmin && (
                            <Badge className="bg-red-600 text-xs">Sysadmin</Badge>
                          )}
                          {user.is_admin && !user.is_sysadmin && !user.is_operator_admin && (
                            <Badge className="bg-amber-600 text-xs">Admin</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-400 text-sm">
                        {user.created_at ? formatDistanceToNow(new Date(user.created_at), { addSuffix: true }) : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(user.id)}
                            className="text-slate-300 hover:text-white"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          {!user.email_verified && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleVerifyEmail(user.id)}
                              className="text-green-400 hover:text-green-300"
                              title="Verify Email"
                            >
                              <MailCheck className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <p className="text-sm text-slate-400">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    className="border-slate-600"
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    className="border-slate-600"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Email Verified</Label>
                <Switch
                  checked={selectedUser.email_verified || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, email_verified: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Admin</Label>
                <Switch
                  checked={selectedUser.is_admin || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_admin: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Operator Admin (VBB)</Label>
                <Switch
                  checked={selectedUser.is_operator_admin || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_operator_admin: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Sysadmin</Label>
                <Switch
                  checked={selectedUser.is_sysadmin || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_sysadmin: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Beta User</Label>
                <Switch
                  checked={selectedUser.is_beta_user || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_beta_user: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Beta Membership Active</Label>
                <Switch
                  checked={selectedUser.beta_membership_active || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, beta_membership_active: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Platform Banned</Label>
                <Switch
                  checked={selectedUser.is_platform_banned || false}
                  onCheckedChange={(checked) => setSelectedUser({...selectedUser, is_platform_banned: checked})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={() => handleUpdateUser(selectedUser.id, {
                email_verified: selectedUser.email_verified,
                is_admin: selectedUser.is_admin,
                is_operator_admin: selectedUser.is_operator_admin,
                is_sysadmin: selectedUser.is_sysadmin,
                is_beta_user: selectedUser.is_beta_user,
                beta_membership_active: selectedUser.beta_membership_active,
                is_platform_banned: selectedUser.is_platform_banned
              })}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagementPanel;
